import { COMPARISON_OPERATORS } from '@witmetrics/utils/constants';
import { buildColumnFilter } from '@witmetrics/utils/filters';
import { asyncMap, mapToKey } from '@witmetrics/utils/arrays';
import { API } from '@/api';
import { fetchProjectFileData } from '@/api/utils/projects';
import { fetchFileCollectionData } from '@/utils/files';
import { DEFAULT_PARAMS } from './constants';

const { EQUALS } = COMPARISON_OPERATORS;

export const classes = {
  overlay:
    'w-[96vw] max-w-[1450px] px-6 py-4 gap-4 flex flex-col h-full overflow-hidden',
  content: 'flex w-full gap-4 grow overflow-hidden',
  leftColumn: 'h-full shrink-0 w-[475px] flex flex-col',
  loading:
    'h-full place-center text-xl text-grey-6 dark:text-mauve-3 font-normal',
  divider: 'mt-4 mb-0',
};

export async function fetchPatientProjects(leadId: number) {
  return (
    await API.UnisonProjects.fetchUnisonProjects({
      filters: [buildColumnFilter('userId', EQUALS.value, leadId)],
      pagination: { page: 1, pageSize: 100 }, // TODO: Pagination
    })
  ).results;
}

export async function fetchPracticeData(practiceId: number) {
  const [practiceUsers, keypointTypes] = await Promise.all([
    fetchPracticeUsers(practiceId),
    fetchKeypointTypes(practiceId),
  ]);
  return { practiceUsers, keypointTypes };
}

export async function fetchProjectsData(projectIds: number[]) {
  const data = await asyncMap(projectIds, fetchProjectData);
  return {
    tasks: mapToKey(data, 'tasks').flat(),
    notes: mapToKey(data, 'notes').flat(),
    keypoints: mapToKey(data, 'keypoints').flat(),
    orders: mapToKey(data, 'orders').flat(),
    services: mapToKey(data, 'services').flat(),
  };
}

export async function fetchFilesData(projectIds: number[]) {
  const data = await asyncMap(projectIds, fetchProjectFiles);
  return {
    projectFileCollections: mapToKey(data, 'projectFileCollections').flat(),
    fileCollectionFiles: mapToKey(data, 'fileCollectionFiles').flat(),
    fileNotes: mapToKey(data, 'fileNotes').flat(),
    fileCollections: mapToKey(data, 'fileCollections').flat(),
  };
}

async function fetchProjectData(projectId: number) {
  const [tasks, notes, keypoints, orders, services] = await Promise.all([
    fetchProjectTasks(projectId),
    fetchProjectNotes(projectId),
    fetchProjectKeypoints(projectId),
    fetchProjectOrders(projectId),
    fetchProjectServices(projectId),
  ]);
  return {
    tasks,
    notes,
    keypoints,
    orders,
    services,
  };
}

async function fetchProjectTasks(projectId: number) {
  return (
    await API.UnisonProjects.fetchUnisonProjectTasks(projectId, DEFAULT_PARAMS)
  ).results;
}

async function fetchProjectNotes(projectId: number) {
  return (
    await API.UnisonProjects.fetchUnisonProjectNotes(projectId, DEFAULT_PARAMS)
  ).results;
}

async function fetchProjectKeypoints(projectId: number) {
  return (
    await API.UnisonProjects.fetchUnisonProjectKeypoints(
      projectId,
      DEFAULT_PARAMS
    )
  ).results;
}

async function fetchProjectOrders(projectId: number) {
  return (
    await API.UnisonProjects.fetchUnisonProjectOrders(projectId, DEFAULT_PARAMS)
  ).results;
}

async function fetchProjectServices(projectId: number) {
  return (
    await API.UnisonProjects.fetchUnisonProjectScanServices(
      projectId,
      DEFAULT_PARAMS
    )
  ).results;
}

async function fetchPracticeUsers(practiceId: number) {
  return (
    await API.Practices.fetchPracticeUsers(practiceId, {
      filters: [],
      pagination: { page: 1, pageSize: 100 },
    })
  ).results;
}

async function fetchKeypointTypes(practiceId: number) {
  return (
    await API.Practices.fetchPracticeKeypointTypes(practiceId, {
      pagination: { page: 1, pageSize: 250 }, // TODO: Just doing a giant page size to fetch everything
    })
  ).results;
}

async function fetchProjectFiles(projectId: number) {
  const { projectFileCollections, fileCollectionFiles } =
    await fetchProjectFileData(projectId);
  const { files, fileCollections } = await fetchFileCollectionData(
    mapToKey(projectFileCollections, 'fileCollectionId')
  );
  const fileNotes = await asyncMap(files, ({ fileId }) =>
    API.Files.fetchFileNotes(fileId, {
      pagination: { page: 1, pageSize: 100 }, // TODO: Pagination could be improved
    })
  );
  return {
    projectFileCollections,
    fileCollectionFiles,
    fileNotes: fileNotes.map((note) => note.results).flat(),
    fileCollections,
  };
}
